/**
 * Validate a ?redirect= query param before handing it to the router.
 *
 * A prefix check like startsWith("/") && !startsWith("//") looks sufficient but
 * is not. Under the WHATWG URL spec a backslash is treated as a slash when
 * resolving a relative URL against an http(s) base, so "/\evil.com" resolves to
 * the network path reference "//evil.com", a different origin. That value then
 * reaches history.pushState through navigate() and either throws a SecurityError
 * (killing the login flow) or navigates off site, depending on the browser.
 *
 * Rather than blacklisting the escapes we know about, resolve the candidate
 * against the real origin and keep it only if the result is still same origin.
 * Returns the normalised internal path, or null when the param is missing or
 * unsafe. Callers should treat null as "no redirect requested".
 *
 * @param {string|null|undefined} value raw value of the redirect query param
 * @returns {string|null} a path beginning with "/", or null
 */
export function getSafeRedirect(value) {
  if (!value || typeof value !== "string") return null;

  // Must look like a site relative path up front. This rejects absolute URLs,
  // scheme relative URLs, and anything with a scheme such as javascript:.
  if (!value.startsWith("/")) return null;

  try {
    const origin = window.location.origin;
    const resolved = new URL(value, origin);
    if (resolved.origin !== origin) return null;
    return `${resolved.pathname}${resolved.search}${resolved.hash}`;
  } catch {
    return null;
  }
}

/**
 * Build the "?redirect=..." query string to carry a validated redirect between
 * the login and signup pages, or an empty string when there is nothing to carry.
 *
 * @param {string|null} safeRedirect output of getSafeRedirect
 * @returns {string}
 */
export function buildRedirectQuery(safeRedirect) {
  return safeRedirect ? `?redirect=${encodeURIComponent(safeRedirect)}` : "";
}
